<script lang="ts" setup>
import {defineAsyncComponent, nextTick, ref} from "vue";

import {FaceSmileIcon, PaperAirplaneIcon, XCircleIcon} from "@heroicons/vue/24/outline";
import IconButton from "@src/components/ui/inputs/IconButton.vue";
import {useChatSessionStore} from "@src/stores/chatSession";
import {useMessageStore} from "@src/stores/messages";

const EmojiPicker = defineAsyncComponent(() => import("@src/components/ui/inputs/EmojiPicker/EmojiPicker.vue"));
import ScaleTransition from "@src/components/ui/transitions/ScaleTransition.vue";
import Swal from "sweetalert2";
import MessageService from "@src/services/message"
import {useIsMobile} from "@src/composables/useIsMobile";

const props = defineProps<{
    canSend: boolean
}>()

const emit = defineEmits<{
    sendMessage: []
}>()

const sessionStore = useChatSessionStore()
const messageStore = useMessageStore()
const {isMobile} = useIsMobile()

const oldMessage = sessionStore.selectedMessage?.message;
const newMessage = ref<string>(sessionStore.selectedMessage?.message ?? '');

const showPicker = ref(false);
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const MAX_HEIGHT = 80;

const handleInput = () => {
    const el = textareaRef.value;
    if (!el) return;

    el.style.height = "auto";
    const newHeight = Math.min(el.scrollHeight, MAX_HEIGHT);
    el.style.height = newHeight + "px";
    el.style.overflowY = el.scrollHeight > MAX_HEIGHT ? "auto" : "hidden";
};

const selectEmoji = (emoji: string) => {
    messageStore.message += emoji;
    showPicker.value = false;

    nextTick(() => {
        if (textareaRef.value) {
            textareaRef.value.focus();
            const len = textareaRef.value.value.length;
            textareaRef.value.setSelectionRange(len, len);
        }
    });
}

const handleClickOutside = (event: Event) => {
    const target = event.target as HTMLElement;
    const parent = target.parentElement as HTMLElement;

    if (
        target &&
        !target.classList.contains("toggle-picker-button") &&
        parent &&
        !parent.classList.contains("toggle-picker-button")
    ) {
        showPicker.value = false;
    }
};

const cancel = () => {
    sessionStore.inputMode = "normal";
    sessionStore.selectedMessage = undefined;
}

const editMessage = () => {
    MessageService.editMessage(
        (sessionStore.selectedMessage?.id ?? 0),
        newMessage.value ?? ''
    )
    .then(response => {
        if(response.data.success) {
            if(sessionStore.selectedMessage) {
                sessionStore.selectedMessage.message = newMessage.value ?? '';
                sessionStore.selectedMessage.is_edited = true;
            }
            sessionStore.inputMode = "normal";
            sessionStore.selectedMessage = undefined;
        } else {
            Swal.fire({
                icon: 'error',
                title: 'Ocorreu um erro ao editar a mensagem!',
            });
        }
    }).catch(error => {
        Swal.fire({
            icon: 'error',
            title: 'Ocorreu um erro ao editar a mensagem!',
        });
    })
}

const handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey && !isMobile.value) {
        event.preventDefault();
        editMessage();
    }
}

defineExpose({ textareaRef })
</script>

<template>
    <div class="w-full h-full flex flex-col justify-center">
        <div class="bg-green-700 flex m-2 p-2 rounded text-sm gap-2">
            <svg style="width: 18px; margin: 10px; fill: white;" viewBox="0 0 512 512"
                 xmlns="http://www.w3.org/2000/svg">
                <!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.-->
                <path
                    d="M410.3 231l11.3-11.3-33.9-33.9-62.1-62.1L291.7 89.8l-11.3 11.3-22.6 22.6L58.6 322.9c-10.4 10.4-18 23.3-22.2 37.4L1 480.7c-2.5 8.4-.2 17.5 6.1 23.7s15.3 8.5 23.7 6.1l120.3-35.4c14.1-4.2 27-11.8 37.4-22.2L387.7 253.7 410.3 231zM160 399.4l-9.1 22.7c-4 3.1-8.5 5.4-13.3 6.9L59.4 452l23-78.1c1.4-4.9 3.8-9.4 6.9-13.3l22.7-9.1 0 32c0 8.8 7.2 16 16 16l32 0zM362.7 18.7L348.3 33.2 325.7 55.8 314.3 67.1l33.9 33.9 62.1 62.1 33.9 33.9 11.3-11.3 22.6-22.6 14.5-14.5c25-25 25-65.5 0-90.5L453.3 18.7c-25-25-65.5-25-90.5 0zm-47.4 168l-144 144c-6.2 6.2-16.4 6.2-22.6 0s-6.2-16.4 0-22.6l144-144c6.2-6.2 16.4-6.2 22.6 0s6.2 16.4 0 22.6z"/>
            </svg>
            <div class="flex flex-1 flex-col text-white">
                <div class="font-bold">Editar mensagem:</div>
                <div>{{ oldMessage }}</div>
            </div>
            <XCircleIcon class="cursor-pointer hover:bg-green-500 transition-all rounded-full px-2 w-8" style="color: white" @click="cancel()" />
        </div>
        <div class="w-full h-full flex flex-row items-end">
            <!-- message textarea -->
            <div class="grow md:mr-5 xs:mr-4 self-end">
                <div
                    class="relative flex flex-row items-center gap-2
                 border border-gray-300 dark:border-gray-600
                 rounded-2xl px-4 py-4
                 focus-within:border-indigo-400
                 transition-colors bg-white dark:bg-gray-900"
                >
                <textarea
                    ref="textareaRef"
                    v-model="newMessage"
                    :disabled="!props.canSend"
                    :placeholder="!props.canSend ? 'Canal desconectado — não é possível enviar mensagens.' : (isMobile ? 'Escreva a sua mensagem...' : 'Escreva a sua mensagem... Aperte SHIFT+ENTER para pular uma linha')"
                    class="w-full resize-none bg-transparent outline-none border-0 focus:ring-0 text-sm placeholder:text-gray-400 dark:text-white"
                    rows="1"
                    @input="handleInput"
                    @keydown="handleKeydown"
                />
                </div>
            </div>

            <div class="min-h-[2.75rem] flex flex-row items-center justify-end">
                <!-- emoji button -->
                <IconButton
                    aria-label="toggle emoji picker"
                    class="ic-btn-ghost-primary cursor-pointer toggle-picker-button w-7 h-7 md:mr-5 xs:mr-4"
                    @click="showPicker = !showPicker"
                >
                    <XCircleIcon v-if="showPicker" class="w-[1.25rem] h-[1.25rem]"/>
                    <FaceSmileIcon v-else class="w-[1.25rem] h-[1.25rem]"/>
                </IconButton>
                <!-- send message button -->
                <IconButton
                    v-if="newMessage.length > 0"
                    aria-label="send message"
                    class="ic-btn-contained-primary cursor-pointer w-7 h-7 active:scale-110 transition-transform duration-300"
                    title="send message"
                    @click="editMessage()"
                >
                    <PaperAirplaneIcon class="w-4.25 h-4.25"/>
                </IconButton>
            </div>

            <ScaleTransition>
                <div
                    v-show="showPicker"
                    v-click-outside="handleClickOutside"
                    class="absolute z-10 bottom-13.75 md:right-0 xs:right-[-5rem] mt-2"
                >
                    <div role="none">
                        <EmojiPicker v-if="showPicker" :show="showPicker" @select="selectEmoji"/>
                    </div>
                </div>
            </ScaleTransition>
        </div>
    </div>
</template>
